import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import Seo from "@/components/Seo";
import { CASE_STUDIES, SEO_DEFAULTS } from "@/data/content";

export default function CaseStudyDetail() {
  const { slug } = useParams();
  const study = useMemo(() => CASE_STUDIES.find((c) => c.slug === slug), [slug]);

  if (!study) {
    return (
      <div className="pt-40 pb-24 px-6 text-center text-[#3F4A6B]">
        <Seo title="Case study not found" noindex />
        <div className="font-display text-3xl mb-4">Case study not found.</div>
        <Link to="/case-studies" className="text-gold underline">Return to engagements</Link>
      </div>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: study.headline,
    image: study.image,
    publisher: { "@type": "Organization", name: "AlchamisGroup", url: SEO_DEFAULTS.baseUrl },
  };

  return (
    <div data-testid={`case-detail-${study.slug}`} className="pt-32 pb-24">
      <Seo
        title={study.headline}
        description={study.summary}
        path={`/case-studies/${study.slug}`}
        image={study.image}
        type="article"
        jsonLd={jsonLd}
      />

      <section className="max-w-[1400px] mx-auto px-6 md:px-12">
        <Link to="/case-studies" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#6B7390] hover:text-gold">
          <ArrowLeft size={14} /> All Engagements
        </Link>
        <div className="mt-10 grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 lg:col-span-7">
            <div className="number-badge">{study.industry} · {study.duration}</div>
            <h1 className="font-display text-5xl sm:text-6xl text-[#001B54] mt-6 leading-[1.04]">{study.headline}</h1>
            <p className="mt-8 text-[#3F4A6B] text-lg leading-relaxed max-w-2xl">{study.summary}</p>
            <div className="mt-8 flex flex-wrap gap-2">
              {study.practices.map((p) => (
                <span key={p} className="px-3.5 py-2 border border-gold/30 text-[0.7rem] tracking-[0.14em] uppercase text-gold/90">{p}</span>
              ))}
            </div>
          </div>
          <div className="col-span-12 lg:col-span-5">
            <div className="aspect-[4/3] overflow-hidden border border-[#001B54]/10">
              <img src={study.image} alt={study.headline} className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="mt-20 max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-8">
        <div className="col-span-12 lg:col-span-6">
          <span className="overline">Challenge</span>
          <p className="font-display text-2xl text-[#001B54] mt-4 leading-relaxed">{study.challenge}</p>
        </div>
        <div className="col-span-12 lg:col-span-5 lg:col-start-8">
          <span className="overline">Our Approach</span>
          <ul className="mt-4 space-y-4">
            {study.approach.map((a) => (
              <li key={a} className="flex gap-3 items-start border-b border-[#001B54]/10 pb-4">
                <Check size={16} className="text-gold mt-1 shrink-0" />
                <span className="text-[#0A1228] leading-relaxed">{a}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="mt-20 max-w-[1400px] mx-auto px-6 md:px-12">
        <span className="overline">Outcomes</span>
        <h2 className="font-display text-4xl text-[#001B54] mt-6 leading-tight">Measured at the board level.</h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#001B54]/8">
          {study.outcomes.map((o) => (
            <div key={o.label} className="bg-white p-10 text-center">
              <div className="font-display text-5xl gold-text">{o.value}</div>
              <div className="text-[#3F4A6B] text-sm mt-3 max-w-[180px] mx-auto">{o.label}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-24 max-w-[1100px] mx-auto px-6 md:px-12 text-center">
        <h2 className="font-display text-4xl text-[#001B54] leading-tight">A similar engagement?</h2>
        <p className="text-[#3F4A6B] mt-4 max-w-xl mx-auto">Request a private briefing — every engagement begins with executive intake.</p>
        <div className="mt-8 flex justify-center gap-4 flex-wrap">
          <Link to="/contact" className="luxury-button">Request a Briefing <ArrowRight size={14} /></Link>
          <Link to="/ai-advisor" className="luxury-button-ghost">Begin Diagnostic</Link>
        </div>
      </section>
    </div>
  );
}
